import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useEffectiveRole } from "@/hooks/useEffectiveRole";

export function RoleGate() {
  const navigate = useNavigate();
  const { role: effectiveRole, loading } = useEffectiveRole();
  const [hasSession, setHasSession] = useState<boolean | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setHasSession(!!data.session);
    });
  }, []);

  useEffect(() => {
    // Wait for session check to complete
    if (hasSession === null) return;

    // No session? Go to login
    if (!hasSession) {
      console.log("[RoleGate] No session, redirecting to /login");
      navigate("/login", { replace: true });
      return;
    }

    // CRITICAL: Never redirect while role is still loading
    if (loading) return;

    console.log("[RoleGate] routing", { effectiveRole, loading });

    // Redirect based on role
    if (effectiveRole === "admin") {
      navigate("/admin", { replace: true });
    } else if (effectiveRole === "portfolio_manager") {
      navigate("/pm", { replace: true });
    } else if (effectiveRole === "client_admin") {
      navigate("/client/sub-orgs", { replace: true });
    } else if (effectiveRole === "org_admin") {
      navigate("/org-admin", { replace: true });
    } else if (effectiveRole === "master_reviewer" || effectiveRole === "reviewer") {
      navigate("/reviewer", { replace: true });
    } else if (effectiveRole === "respondent") {
      navigate("/dashboard", { replace: true });
    }
    // If no role recognized, stay on this page (no-role screen shown below)
  }, [hasSession, effectiveRole, loading, navigate]);

  // Show loading while session or role is being determined
  if (hasSession === null || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Has session but no role — show error, do NOT redirect to /login
  if (hasSession && !effectiveRole) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-amber-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold mb-2">No Role Assigned</h2>
          <p className="text-muted-foreground mb-4">
            Your account does not have a role assigned. Please contact your administrator.
          </p>
          <button
            onClick={() => {
              supabase.auth.signOut().then(() => {
                window.location.href = "/login";
              });
            }}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90 transition-opacity"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // Default loading (redirect will happen via useEffect)
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}
